import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { Logo } from "@/components/common/Logo";
import api, { humanError } from "@/lib/api";

/**
 * Confirm an email address from a link.
 *
 * Verifies on load, since the person already acted by clicking the link and a
 * second button to press would be a pointless step. The guard ref stops React's
 * development double-effect from spending the single-use token twice, which
 * would show a failure on a link that actually worked.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "checking" : "missing");
  const [message, setMessage] = useState("");
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    api.post("/auth/email/verify", { token })
      .then(({ data }) => {
        setMessage(data.email || "");
        setState("done");
      })
      .catch((err) => {
        setMessage(humanError(err));
        setState("failed");
      });
  }, [token]);

  return (
    <div className="hs-mint-gradient flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-[#E4E9EF] bg-white p-8 text-center hs-card-shadow" data-testid={`verify-${state}`}>
          {state === "checking" ? (
            <>
              <div className="mx-auto mb-4 h-11 w-11 animate-pulse rounded-full bg-[#EEF3FB]" />
              <h1 className="font-display text-2xl font-bold text-[#0B2B33]">Confirming your address…</h1>
            </>
          ) : state === "done" ? (
            <>
              <span className="mb-4 inline-grid h-11 w-11 place-items-center rounded-full bg-[#E8FBF1] text-[#16A46B]">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <h1 className="font-display text-2xl font-bold text-[#0B2B33]">Address confirmed</h1>
              <p className="mt-2 text-sm text-[#43535E]">
                {message ? <><span className="font-semibold text-[#0B2B33]">{message}</span> is verified. </> : null}
                You will now get approval and publishing updates by email.
              </p>
              <Link to="/app" className="hs-btn-primary mt-6 inline-flex w-full items-center justify-center">
                Go to the dashboard
              </Link>
            </>
          ) : (
            <>
              <span className="mb-4 inline-grid h-11 w-11 place-items-center rounded-full bg-[#FDECEC] text-[#E3252A]">
                <XCircle className="h-5 w-5" />
              </span>
              <h1 className="font-display text-2xl font-bold text-[#0B2B33]">
                {state === "missing" ? "This link is incomplete" : "This link no longer works"}
              </h1>
              <p className="mt-2 text-sm text-[#43535E]">
                {state === "missing"
                  ? "Open the link from your email exactly as it was sent."
                  : message || "It may have expired or already been used."}
              </p>
              <p className="mt-4 text-sm text-[#43535E]">
                Sign in and ask for a new one from your settings.
              </p>
              <Link to="/login" className="hs-btn-primary mt-6 inline-flex w-full items-center justify-center">
                Sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
